import { useCallback, useMemo } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import {
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  ReactFlow,
  type Edge,
  type Node,
  type NodeChange,
  type NodeProps,
  applyNodeChanges,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AppImage } from '../common/AppImage';
import type { WorldElementDto } from '../../api/queries/worlds';

interface WorldCanvasProps {
  elements: WorldElementDto[];
  selectedElementId: string | null;
  onSelectElement: (id: string) => void;
  onUpdatePosition?: (id: string, x: number, y: number) => void;
}

const ELEMENT_TYPE_ICONS: Record<string, string> = {
  magic_system: '\u2728',
  ability: '\u26A1',
  being: '\uD83D\uDC64',
  creature: '\uD83D\uDC09',
  country: '\uD83C\uDFD4\uFE0F',
  culture: '\uD83C\uDFAD',
  deity: '\uD83D\uDD31',
  ecosystem: '\uD83C\uDF3F',
  economy: '\uD83D\uDCB0',
  government: '\uD83C\uDFDB\uFE0F',
  history: '\uD83D\uDCDC',
  language: '\uD83D\uDDE3\uFE0F',
  law: '\u2696\uFE0F',
  location: '\uD83D\uDCCD',
  magic: '\uD83E\uDE84',
  mythology: '\uD83D\uDCD6',
  religion: '\uD83D\uDE4F',
  society: '\uD83C\uDFE0',
  technology: '\u2699\uFE0F',
};

type WorldElementNodeData = {
  element: WorldElementDto;
  selected: boolean;
  childCount: number;
  onSelect: (id: string) => void;
};

type WorldElementNodeType = Node<WorldElementNodeData, 'worldElement'>;

function WorldElementNode({ data }: NodeProps<WorldElementNodeType>) {
  const { element, selected, childCount, onSelect } = data;
  const icon = ELEMENT_TYPE_ICONS[element.elementType] ?? '\uD83D\uDD35';
  const description = element.description
    ? element.description.length > 80
      ? element.description.slice(0, 80) + '...'
      : element.description
    : null;

  return (
    <>
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Box
        onClick={() => onSelect(element.id)}
        sx={{
          width: 200,
          p: 1.5,
          bgcolor: 'background.paper',
          border: 2,
          borderColor: selected ? 'primary.main' : 'divider',
          borderRadius: 1.5,
          cursor: 'pointer',
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: selected ? 'primary.main' : 'action.hover',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="body2" sx={{ lineHeight: 1 }}>
            {icon}
          </Typography>
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ flex: 1, fontWeight: 'bold' }}
          >
            {element.name}
          </Typography>
        </Box>

        {description && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              mb: 0.5,
            }}
          >
            {description}
          </Typography>
        )}

        {element.imageUrl && (
          <Box sx={{ mt: 0.5, mb: 0.5 }}>
            <AppImage
              src={element.imageUrl}
              alt={element.name}
              width="100%"
              height={60}
              objectFit="cover"
            />
          </Box>
        )}

        {childCount > 0 && (
          <Chip
            size="small"
            label={`${childCount} sub-element${childCount === 1 ? '' : 's'}`}
            sx={{ mt: 0.5 }}
          />
        )}
      </Box>
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
    </>
  );
}

const nodeTypes = { worldElement: WorldElementNode };

export function WorldCanvas({
  elements,
  selectedElementId,
  onSelectElement,
  onUpdatePosition,
}: WorldCanvasProps) {
  const childCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const el of elements) {
      if (el.parentId) {
        counts.set(el.parentId, (counts.get(el.parentId) ?? 0) + 1);
      }
    }
    return counts;
  }, [elements]);

  const nodes: Node[] = useMemo(
    () =>
      elements.map((el, index) => ({
        id: el.id,
        type: 'worldElement' as const,
        position: { x: (index % 4) * 280, y: Math.floor(index / 4) * 200 },
        data: {
          element: el,
          selected: el.id === selectedElementId,
          childCount: childCounts.get(el.id) ?? 0,
          onSelect: onSelectElement,
        },
      })),
    [elements, selectedElementId, childCounts, onSelectElement],
  );

  const edges: Edge[] = useMemo(
    () =>
      elements
        .filter((el) => el.parentId)
        .map((el) => ({
          id: `${el.parentId}-${el.id}`,
          source: el.parentId!,
          target: el.id,
          type: 'smoothstep',
          animated: true,
        })),
    [elements],
  );

  const onNodesChange = useCallback(
    (changes: NodeChange[]) => {
      // We only care about position changes for persisting, but we need to
      // apply all changes so ReactFlow stays interactive.
      void applyNodeChanges(changes, nodes);

      if (!onUpdatePosition) return;
      for (const change of changes) {
        if (change.type === 'position' && change.position && !change.dragging) {
          onUpdatePosition(change.id, change.position.x, change.position.y);
        }
      }
    },
    [nodes, onUpdatePosition],
  );

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 500,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        fitView
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} />
        <Controls position="bottom-left" />
        <MiniMap position="bottom-right" pannable zoomable />
      </ReactFlow>
    </Box>
  );
}
